/*
 * LogisticElementForm.tsx : Un formulaire pour ajouter ou modifier un élément logistique.
 */

// Import React
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// Import FontAwesome
import {
	faClipboardList,
	faClock,
	faCoins,
	faInfoCircle,
	faLink,
} from '@fortawesome/free-solid-svg-icons';

// Import Communs et Layout
import {
	DatePickerField,
	InputField,
	SelectField,
	AlertDialog,
	finishFormSave,
	FormSaveActions,
	useFormDirty,
} from '../../../common';
import CardHeader from '../../../layout/CardHeader';
import CardSection from '../../../layout/CardSection';

// Import logisticElements
import { LogisticElementData, TaxRateData } from '../types/types';
import { logisticElementsApi } from '../services/logisticElementsApi';
import { servicesApi } from 'features/services/services/servicesApi';
import { productsApi } from 'features/products';
import { projectsApi } from 'features/projects/services/projectsApi';
import { taxRatesApi } from 'features/taxRate';
import { customerInvoicesApi } from 'features/customerInvoices/services/customerInvoicesApi';
import { creditNotesApi } from 'features/creditNotes/services/creditNotesApi';
import { expensesApi } from 'features/expenses/services/expensesApi';
import { ServiceData } from 'features/services';
import { ProductData } from 'features/products';

// Composant LogisticElementForm
const LogisticElementForm: React.FC = () => {
	// Hooks de State
	const [loading, setLoading] = React.useState(true);
	const [baselineReady, setBaselineReady] = React.useState(false);
	const userId = localStorage.getItem('userId');
	const userName = localStorage.getItem('name');
	const userFirstName = localStorage.getItem('firstName');
	const [formData, setFormData] = React.useState<LogisticElementData>({
		id: 0,
		date: new Date(),
		unitPriceExclTax: 0,
		quantity: 1,
		service: null,
		product: null,
		project: null,
		customerInvoice: null,
		creditNote: null,
		createdBy: userId
			? {
					id: Number(userId),
					name: userName || '',
					firstName: userFirstName || '',
				}
			: undefined,
		createdAt: new Date()
			.toISOString()
			.replace('Z', '+00:00') as unknown as Date,
	});

	// Hooks select service
	const [serviceOptions, setServiceOptions] = React.useState<
		{ key: string; label: string }[]
	>([]);
	const [services, setServices] = React.useState<ServiceData[]>([]);
	const [loadingServices, setLoadingServices] = React.useState(true);
	const [isServiceDisabled, setIsServiceDisabled] = React.useState(false);

	// Hooks select product
	const [productOptions, setProductOptions] = React.useState<
		{ key: string; label: string }[]
	>([]);
	const [products, setProducts] = React.useState<ProductData[]>([]);
	const [loadingProducts, setLoadingProducts] = React.useState(true);
	const [isProductDisabled, setIsProductDisabled] = React.useState(false);

	// Hooks select project
	const [projectOptions, setProjectOptions] = React.useState<
		{ key: string; label: string }[]
	>([]);
	const [loadingProjects, setLoadingProjects] = React.useState(true);

	// Hooks select customerInvoice
	const [customerInvoiceOptions, setCustomerInvoiceOptions] = React.useState<
		{ key: string; label: string }[]
	>([]);
	const [loadingCustomerInvoices, setLoadingCustomerInvoices] =
		React.useState(true);

	// Hooks select creditNote
	const [creditNoteOptions, setCreditNoteOptions] = React.useState<
		{ key: string; label: string }[]
	>([]);
	const [loadingCreditNotes, setLoadingCreditNotes] = React.useState(true);

	// Hooks select expense (dépense liée à cet élément logistique de refacturation)
	const [expenseOptions, setExpenseOptions] = React.useState<
		{ key: string; label: string }[]
	>([]);
	const [loadingExpenses, setLoadingExpenses] = React.useState(true);
	// Identifiant de la dépense originale (pour déliage si changement)
	const [originalExpenseId, setOriginalExpenseId] = React.useState<
		number | null
	>(null);

	// Hooks select taxRate
	const [taxRateOptions, setTaxRateOptions] = React.useState<
		{ key: string; label: string }[]
	>([]);
	const [taxRates, setTaxRates] = React.useState<TaxRateData[]>([]);
	const [loadingTaxRates, setLoadingTaxRates] = React.useState(true);

	const unitPriceInclTax = React.useMemo(() => {
		if (!formData.unitPriceExclTax) return 0;
		// TTC = HT * (1 + taux)
		return (
			Number(formData.unitPriceExclTax) *
			Number(formData.quantity || 1) *
			(1 + (formData.taxRate?.rate || 0))
		);
	}, [formData.unitPriceExclTax, formData.quantity, formData.taxRate?.rate]);

	// Hook pour gérer l'ouverture de l'alerte
	const [isAlertOpen, setIsAlertOpen] = React.useState(false);

	// Hook de Navigation et Paramètres de navigation
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();
	const { isDirty, markClean } = useFormDirty(formData, baselineReady);

	// Une fois le composant monté et si un id est présent, fetch des données et préremplissage du formulaire
	React.useEffect(() => {
		// Vérifier la présence du token d'authentification
		const token = localStorage.getItem('token');
		if (!token) return;

		if (id) {
			logisticElementsApi
				.get(Number(id), token)
				.then((data) => {
					setFormData(data);
					// Préremplir les selects
					if (data.service) {
						setServiceOptions([
							{
								key: data.service.id.toString(),
								label: data.service.label,
							},
						]);
					}
					if (data.product) {
						setProductOptions([
							{
								key: data.product.id.toString(),
								label: data.product.label,
							},
						]);
					}
					if (data.project) {
						setProjectOptions([
							{
								key: data.project.id.toString(),
								label: data.project.referenceNumber,
							},
						]);
					}
					if (data.customerInvoice) {
						setCustomerInvoiceOptions([
							{
								key: data.customerInvoice.id.toString(),
								label: data.customerInvoice.referenceNumber,
							},
						]);
					}
					if (data.creditNote) {
						setCreditNoteOptions([
							{
								key: data.creditNote.id.toString(),
								label: data.creditNote.referenceNumber,
							},
						]);
					}
					if (data.taxRate) {
						setTaxRateOptions([
							{
								key: data.taxRate.id.toString(),
								label: data.taxRate.label || data.taxRate.code,
							},
						]);
					}
					// Préremplir la dépense liée (inverse de la relation OneToOne)
					if (data.expense) {
						setExpenseOptions([
							{
								key: data.expense.id.toString(),
								label: `${data.expense.id} - ${data.expense.label || 'Sans libellé'}`,
							},
						]);
						setOriginalExpenseId(data.expense.id);
					}
					// Désactiver les selects si non déjà sélectionné
					if (data.service) setIsServiceDisabled(false);
					if (data.product) setIsProductDisabled(false);
				})
				.catch((error) => console.error(error))
				.finally(() => {
					setLoading(false);
					setBaselineReady(true);
				});
		} else {
			// Si pas d'id, on arrête le loading directement
			setLoading(false);
			setBaselineReady(true);
		}
	}, [id]);

	// Lazy load des options services en arrière-plan
	React.useEffect(() => {
		const token = localStorage.getItem('token');
		if (!token) return;

		if (!loading && loadingServices) {
			servicesApi
				.listPaginated(token, { limit: 999999 })
				.then((res) => {
					setServiceOptions(
						res.data.map((service) => ({
							key: service.id.toString(),
							label: service.label,
						}))
					);
					setServices(res.data);
				})
				.catch(console.error)
				.finally(() => setLoadingServices(false));
		}
	}, [loading, loadingServices]);

	// Lazy load des options produits en arrière-plan
	React.useEffect(() => {
		const token = localStorage.getItem('token');
		if (!token) return;

		if (!loading && loadingProducts) {
			productsApi
				.listPaginated(token, { limit: 999999 })
				.then((res) => {
					setProductOptions(
						res.data.map((product) => ({
							key: product.id.toString(),
							label: product.label,
						}))
					);
					setProducts(res.data);
				})
				.catch(console.error)
				.finally(() => setLoadingProducts(false));
		}
	}, [loading, loadingProducts]);

	// Lazy load des options projets en arrière-plan (route /select allégée)
	React.useEffect(() => {
		const token = localStorage.getItem('token');
		if (!token) return;

		if (!loading && loadingProjects) {
			projectsApi
				.listSelect(token)
				.then((items) => {
					setProjectOptions(
						items.map((item) => ({
							key: item.id.toString(),
							label: String(item.referenceNumber),
						}))
					);
				})
				.catch(console.error)
				.finally(() => setLoadingProjects(false));
		}
	}, [loading, loadingProjects]);

	// Lazy load des options factures clients en arrière-plan
	React.useEffect(() => {
		const token = localStorage.getItem('token');
		if (!token) return;

		if (!loading && loadingCustomerInvoices) {
			customerInvoicesApi
				.listSelect(token)
				.then((items) => {
					setCustomerInvoiceOptions(
						items.map((item) => ({
							key: item.id.toString(),
							label: String(item.referenceNumber),
						}))
					);
				})
				.catch(console.error)
				.finally(() => setLoadingCustomerInvoices(false));
		}
	}, [loading, loadingCustomerInvoices]);

	// Lazy load des options creditNotes en arrière-plan
	React.useEffect(() => {
		const token = localStorage.getItem('token');
		if (!token) return;

		if (!loading && loadingCreditNotes) {
			creditNotesApi
				.listSelect(token)
				.then((items) => {
					setCreditNoteOptions(
						items.map((item) => ({
							key: item.id.toString(),
							label: String(item.referenceNumber),
						}))
					);
				})
				.catch(console.error)
				.finally(() => setLoadingCreditNotes(false));
		}
	}, [loading, loadingCreditNotes]);

	// Lazy load des options dépenses en arrière-plan (pour la liaison avec cet élément logistique)
	React.useEffect(() => {
		const token = localStorage.getItem('token');
		if (!token) return;

		if (!loading && loadingExpenses) {
			expensesApi
				.listSelect(token)
				.then((items) => {
					setExpenseOptions(
						items.map((item) => ({
							key: item.id.toString(),
							label: `${item.id} - ${item.label || 'Sans libellé'}`,
						}))
					);
				})
				.catch(console.error)
				.finally(() => setLoadingExpenses(false));
		}
	}, [loading, loadingExpenses]);

	// Lazy load des options taxRates en arrière-plan
	React.useEffect(() => {
		const token = localStorage.getItem('token');
		if (!token) return;

		if (!loading && loadingTaxRates) {
			taxRatesApi
				.listPaginated(token, { limit: 999999 })
				.then((res) => {
					setTaxRateOptions(
						res.data.map((taxRate) => ({
							key: taxRate.id.toString(),
							label: taxRate.label || taxRate.code,
						}))
					);
					setTaxRates(res.data);
				})
				.catch(console.error)
				.finally(() => setLoadingTaxRates(false));
		}
	}, [loading, loadingTaxRates]);

	// Gestion de la soumission du formulaire
	const [isSubmitting, setIsSubmitting] = React.useState(false);
	const handleSave = async (shouldLeave: boolean) => {
		// On vérifie les prérequis
		if ((!formData.product && !formData.service) || !formData.project) {
			setIsAlertOpen(true);
			return;
		}
		// Vérifier la présence du token d'authentification
		const token = localStorage.getItem('token');
		if (!token) return;
		// Indiquer que la soumission est en cours
		setIsSubmitting(true);

		try {
			// Sauvegarder l'élément logistique (création ou mise à jour)
			let savedId: number;
			const wasCreate = !id;
			if (id) {
				await logisticElementsApi.update(Number(id), formData, token);
				savedId = Number(id);
			} else {
				const created = await logisticElementsApi.create(formData, token);
				savedId = created.id;
			}

			// Gérer la liaison avec la dépense (Expense est le côté propriétaire de la relation)
			const newExpenseId = formData.expense?.id ?? null;

			// Si la dépense a changé, désolidariser l'ancienne
			if (originalExpenseId && originalExpenseId !== newExpenseId) {
				await expensesApi.update(
					originalExpenseId,
					{ logisticElement: null } as never,
					token
				);
			}
			// Lier la nouvelle dépense (si présente)
			if (newExpenseId) {
				await expensesApi.update(
					newExpenseId,
					{ logisticElement: { id: savedId } } as never,
					token
				);
			}
			setOriginalExpenseId(newExpenseId);

			finishFormSave(shouldLeave, {
				onLeave: () => navigate('/app/logisticElements'),
				markClean,
				stayOnEdit:
					wasCreate && savedId
						? () =>
								navigate(`/app/logisticElements/${savedId}/edit`, {
									replace: true,
								})
						: undefined,
			});
		} catch (error) {
			console.error(error);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleSubmit = async (e: React.FormEvent) => {
		// Empêcher le comportement par défaut du formulaire
		e.preventDefault();
		await handleSave(true);
	};

	// Gestion des changements dans le formulaire
	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFormData({ ...formData, [name]: value });
	};

	// Fonctions pour effacer les selects liés
	const handleClearCustomerInvoice = () => {
		setFormData((prev) => ({
			...prev,
			customerInvoice: null,
		}));
	};
	const handleClearCreditNote = () => {
		setFormData((prev) => ({
			...prev,
			creditNote: null,
		}));
	};
	const handleClearExpense = () => {
		setFormData((prev) => ({
			...prev,
			expense: null,
		}));
	};

	// Gestion des changements dans le formulaire pour les selects
	const handleChangeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const { name, value } = e.target;

		if (name === 'service') {
			// Si on a selectionné u nservice, on doit aussi desactiver les produits
			const selectedOption = serviceOptions.find((opt) => opt.key === value);
			if (selectedOption) {
				const service = services.find(
					(s) => s.id === Number(selectedOption.key)
				);
				const defaultPrice = service ? service.defaultPrice || 0.0 : 0.0;
				const defaultTaxRate = service
					? service.defaultTaxRate || formData.taxRate
					: formData.taxRate;
				const taxRateInfo = defaultTaxRate
					? taxRates.find((rate) => rate.code === defaultTaxRate.code)
					: undefined;
				setFormData((prev) => ({
					...prev,
					service: service || prev.service,
					product: null,
					unitPriceExclTax: defaultPrice,
					taxRate: taxRateInfo
						? {
								id: taxRateInfo.id,
								code: taxRateInfo.code,
								label: taxRateInfo.label,
								rate: taxRateInfo.rate,
								country: taxRateInfo.country,
								isActive: taxRateInfo.isActive,
							}
						: undefined,
				}));
				setIsServiceDisabled(false);
				setIsProductDisabled(true);
			}
			return;
		}

		if (name === 'product') {
			const selectedOption = productOptions.find((opt) => opt.key === value);
			if (selectedOption) {
				const product = products.find(
					(p) => p.id === Number(selectedOption.key)
				);
				const defaultPrice = product ? product.defaultPrice || 0.0 : 0.0;
				const defaultTaxRate = product
					? product.defaultTaxRate || formData.taxRate
					: formData.taxRate;
				const taxRateInfo = defaultTaxRate
					? taxRates.find((rate) => rate.code === defaultTaxRate.code)
					: undefined;
				setFormData((prev) => ({
					...prev,
					product: product || prev.product,
					service: null,
					unitPriceExclTax: defaultPrice,
					taxRate: taxRateInfo
						? {
								id: taxRateInfo.id,
								code: taxRateInfo.code,
								label: taxRateInfo.label,
								rate: taxRateInfo.rate,
								country: taxRateInfo.country,
								isActive: taxRateInfo.isActive,
							}
						: undefined,
				}));
				setIsProductDisabled(false);
				setIsServiceDisabled(true);
			}
			return;
		}

		if (name === 'project') {
			const selectedOption = projectOptions.find((opt) => opt.key === value);
			if (selectedOption) {
				setFormData((prev) => ({
					...prev,
					project: {
						id: Number(selectedOption.key),
						referenceNumber: selectedOption.label,
					},
				}));
			}
			return;
		}

		if (name === 'customerInvoice') {
			const selectedOption = customerInvoiceOptions.find(
				(opt) => opt.key === value
			);
			if (selectedOption) {
				setFormData((prev) => ({
					...prev,
					customerInvoice: {
						id: Number(selectedOption.key),
						referenceNumber: selectedOption.label,
					},
				}));
			}
			return;
		}

		if (name === 'creditNote') {
			const selectedOption = creditNoteOptions.find((opt) => opt.key === value);
			if (selectedOption) {
				setFormData((prev) => ({
					...prev,
					creditNote: {
						id: Number(selectedOption.key),
						referenceNumber: selectedOption.label,
					},
				}));
			}
			return;
		}

		if (name === 'taxRate') {
			const selectedOption = taxRateOptions.find((opt) => opt.key === value);
			// On récupère toutes les infos dans taxRates pour pouvoir remplir correctement le formData (notamment le taux de TVA)
			if (selectedOption) {
				const matched = taxRates.find(
					(rate) => (rate.label || rate.code) === selectedOption.label
				);
				setFormData((prev) => ({
					...prev,
					taxRate: {
						id: Number(selectedOption.key),
						code: matched?.code || '',
						label: selectedOption.label,
						rate: matched?.rate || 0,
						country: matched?.country || '',
						isActive: matched?.isActive || false,
					},
				}));
			}
			return;
		}

		if (name === 'expense') {
			const selectedOption = expenseOptions.find((opt) => opt.key === value);
			if (selectedOption) {
				setFormData((prev) => ({
					...prev,
					expense: {
						id: Number(selectedOption.key),
						label: selectedOption.label,
					},
				}));
			}
			return;
		}

		// Mise à jour standard pour les autres champs
		setFormData((prev) => ({ ...prev, [name]: value }));
	};

	// Rendu du composant, le premier div est la card principale
	return (
		<div className="max-w-6xl mx-auto p-6 bg-white rounded-lg shadow-md space-y-6">
			{/* HEADER DE LA CARD */}
			<CardHeader
				icon={faClipboardList}
				title={
					id ? "Édition d'un élément logistique" : 'Nouvel élément logistique'
				}
				createdAt={
					formData.createdAt ? formData.createdAt?.toLocaleString() : undefined
				}
				createdBy={
					formData.createdBy
						? formData.createdBy.firstName + ' ' + formData.createdBy.name
						: undefined
				}
				loading={loading}
			/>

			{/* CONTENU DE LA CARD */}
			<form onSubmit={handleSubmit} className="space-y-6">
				{/* 2 COLONNES */}
				<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
					{/* COLONNE */}
					<CardSection icon={faInfoCircle} title="Informations générales">
						<InputField
							label="Libellé"
							name="label"
							type="text"
							value={formData.label || ''}
							onChange={handleChange}
							loading={loading}
							placeholder='ex: "Renfort quincaillerie suite demande M. Dupont"'
						/>
						<SelectField
							label="Service"
							selectedKey={formData.service?.id?.toString() || ''}
							onValueChange={(val) =>
								handleChangeSelect({
									target: {
										name: 'service',
										value: val,
									},
								} as React.ChangeEvent<HTMLSelectElement>)
							}
							options={serviceOptions}
							loading={loading}
							optionsLoading={loadingServices}
							readOnly={isServiceDisabled}
							entityHref={
								formData.service?.id
									? `/app/settings/services/${formData.service.id}/edit`
									: undefined
							}
							onClear={() =>
								setFormData((prev) => ({
									...prev,
									service: null,
								}))
							}
						/>
						<SelectField
							label="Produits"
							selectedKey={formData.product?.id?.toString() || ''}
							onValueChange={(val) =>
								handleChangeSelect({
									target: {
										name: 'product',
										value: val,
									},
								} as React.ChangeEvent<HTMLSelectElement>)
							}
							options={productOptions}
							loading={loading}
							optionsLoading={loadingProducts}
							readOnly={isProductDisabled}
							entityHref={
								formData.product?.id
									? `/app/settings/products/${formData.product.id}/edit`
									: undefined
							}
							onClear={() =>
								setFormData((prev) => ({
									...prev,
									product: null,
								}))
							}
						/>
						<SelectField
							label="Affaire"
							selectedKey={formData.project?.id?.toString() || ''}
							onValueChange={(val) =>
								handleChangeSelect({
									target: {
										name: 'project',
										value: val,
									},
								} as React.ChangeEvent<HTMLSelectElement>)
							}
							options={projectOptions}
							loading={loading}
							optionsLoading={loadingProjects}
							entityHref={
								formData.project?.id
									? `/app/projects/${formData.project.id}/edit`
									: undefined
							}
							onClear={() =>
								setFormData((prev) => ({
									...prev,
									project: null,
								}))
							}
						/>
					</CardSection>

					{/* COLONNE */}
					<CardSection icon={faCoins} title="Budget">
						<SelectField
							label="Taux de TVA"
							selectedKey={formData.taxRate?.id?.toString() || ''}
							onValueChange={(val) =>
								handleChangeSelect({
									target: {
										name: 'taxRate',
										value: val,
									},
								} as React.ChangeEvent<HTMLSelectElement>)
							}
							options={taxRateOptions}
							loading={loading}
							optionsLoading={loadingTaxRates}
							entityHref={
								formData.taxRate?.id
									? `/app/taxrates/${formData.taxRate.id}/edit`
									: undefined
							}
							onClear={() =>
								setFormData((prev) => ({
									...prev,
									taxRate: null,
								}))
							}
						/>
						<InputField
							label="Tarif unitaire HT"
							name="unitPriceExclTax"
							type="number"
							value={formData.unitPriceExclTax || 0}
							onChange={handleChange}
							loading={loading}
							placeholder='ex: "0.0"'
						/>
						<InputField
							label="Quantité"
							name="quantity"
							type="number"
							value={formData.quantity || 1}
							onChange={handleChange}
							loading={loading}
							placeholder='ex: "1"'
						/>
						<InputField
							label="Total TTC"
							name="unitPriceInclTax"
							type="number"
							value={unitPriceInclTax.toFixed(2)}
							onChange={handleChange}
							loading={loading}
							placeholder="automatique"
							readOnly
						/>
					</CardSection>

					{/* COLONNE */}
					<CardSection icon={faClock} title="Dates et périodicité">
						<DatePickerField
							label="Date"
							name="date"
							value={formData.date ? new Date(formData.date) : undefined}
							onChange={(date) => setFormData({ ...formData, date: date })}
							loading={loading}
						/>
					</CardSection>

					{/* COLONNE */}
					<CardSection icon={faLink} title="Liens associés">
						{/* 2 colonnes pour les selects + boutons de nettoyage alignés à droite */}
						<SelectField
							label="Facture client (si activité facturée)"
							selectedKey={formData.customerInvoice?.id?.toString() || ''}
							onValueChange={(val) =>
								handleChangeSelect({
									target: {
										name: 'customerInvoice',
										value: val,
									},
								} as React.ChangeEvent<HTMLSelectElement>)
							}
							options={customerInvoiceOptions}
							loading={loading}
							optionsLoading={loadingCustomerInvoices}
							entityHref={
								formData.customerInvoice?.id
									? `/app/customerInvoices/${formData.customerInvoice.id}/edit`
									: undefined
							}
							onClear={handleClearCustomerInvoice}
						/>
						<SelectField
							label="Avoir (si activité remboursée)"
							selectedKey={formData.creditNote?.id?.toString() || ''}
							onValueChange={(val) =>
								handleChangeSelect({
									target: {
										name: 'creditNote',
										value: val,
									},
								} as React.ChangeEvent<HTMLSelectElement>)
							}
							options={creditNoteOptions}
							loading={loading}
							optionsLoading={loadingCreditNotes}
							entityHref={
								formData.creditNote?.id
									? `/app/creditNotes/${formData.creditNote.id}/edit`
									: undefined
							}
							onClear={handleClearCreditNote}
						/>
						<SelectField
							label="Dépense associée (refacturation)"
							selectedKey={formData.expense?.id?.toString() || ''}
							onValueChange={(val) =>
								handleChangeSelect({
									target: {
										name: 'expense',
										value: val,
									},
								} as React.ChangeEvent<HTMLSelectElement>)
							}
							options={expenseOptions}
							loading={loading}
							optionsLoading={loadingExpenses}
							entityHref={
								formData.expense?.id
									? `/app/expenses/${formData.expense.id}/edit`
									: undefined
							}
							onClear={handleClearExpense}
						/>
					</CardSection>
				</div>
			</form>

			{/* BOUTONS ANNULER / ENREGISTRER */}
			<FormSaveActions
				isDirty={isDirty}
				isSubmitting={isSubmitting}
				onCancel={() => navigate('/app/logisticElements')}
				onSave={() => handleSave(false)}
				onSaveAndLeave={() => handleSave(true)}
			/>

			{/* Alerte si ni produit ni service sélectionné */}
			<AlertDialog
				label="Attention"
				text="Veuillez sélectionner un produit ou un service et une affaire."
				size="md"
				invert={false}
				hideIcon={false}
				open={isAlertOpen}
				setOpen={setIsAlertOpen}
			/>
		</div>
	);
};

export default LogisticElementForm;
